<script lang="ts">
  import { prefersReducedMotion } from 'svelte/motion';

  interface Props {
    brotando: boolean;
    sway?: number;
  }

  let { brotando, sway = 0 }: Props = $props();

  // Partículas de pétalos y polen desprendidos al florecer
  let petalosBrisa = $state<{ id: number; left: number; delay: number; duration: number; size: number }[]>([]);

  // Interacción táctil en flores
  let florInclinada = $state<number | null>(null);

  function tocarFlor(id: number) {
    florInclinada = id;
    setTimeout(() => {
      if (florInclinada === id) florInclinada = null;
    }, 1200);
  }

  // Capa Fondo: flores más lejanas y pequeñas
  const FLORES_FONDO = [
    { id: 101, left: 4, height: 130, delay: 0.4, rot: -4 },
    { id: 102, left: 24, height: 145, delay: 0.9, rot: 3 },
    { id: 103, left: 42, height: 135, delay: 1.4, rot: -2 },
    { id: 104, left: 62, height: 150, delay: 1.0, rot: 4 },
    { id: 105, left: 82, height: 138, delay: 0.6, rot: -3 },
    { id: 106, left: 94, height: 125, delay: 1.2, rot: 2 },
  ];

  // Capa Media: jazmines silvestres blancos entremezclados
  const JAZMINES_SILVESTRES = [
    { id: 201, left: 14, bottom: 25, delay: 1.1 },
    { id: 202, left: 34, bottom: 20, delay: 1.8 },
    { id: 203, left: 52, bottom: 30, delay: 1.3 },
    { id: 204, left: 74, bottom: 22, delay: 2.0 },
    { id: 205, left: 88, bottom: 26, delay: 1.5 },
  ];

  // Capa Frente: girasoles protagonistas exuberantes con stagger pausado y contemplativo
  const FLORES_FRENTE = [
    { id: 1, left: 10, width: 175, delay: 1.3, rotBase: -3 },
    { id: 2, left: 28, width: 195, delay: 0.7, rotBase: 2 },
    { id: 3, left: 50, width: 230, delay: 0.15, rotBase: 0 }, // Centro majestuoso abre primero
    { id: 4, left: 72, width: 190, delay: 0.8, rotBase: -2 },
    { id: 5, left: 90, width: 170, delay: 1.4, rotBase: 3 },
  ];

  $effect(() => {
    if (brotando && petalosBrisa.length === 0) {
      petalosBrisa = Array.from({ length: 16 }, (_, i) => ({
        id: i,
        left: Math.random() * 92 + 4,
        delay: Math.random() * 4 + 0.2,
        duration: Math.random() * 5 + 6,
        size: Math.random() * 6 + 6,
      }));
    }
  });
</script>

<div class="jardin-escena" class:jardin-visible={brotando} aria-hidden="false">
  <!-- Resplandor dorado primaveral que abraza la tierra -->
  <div class="resplandor-tierra" aria-hidden="true"></div>

  <!-- Pétalos dorados flotando con la brisa -->
  <div class="petalos-viento" aria-hidden="true">
    {#each petalosBrisa as p (p.id)}
      <span
        class="petalo-dorado"
        style="
          left: {p.left}%;
          width: {p.size}px;
          height: {p.size * 1.4}px;
          animation-delay: {p.delay}s;
          animation-duration: {p.duration}s;
        "
      ></span>
    {/each}
  </div>

  <!-- CAPA 1: Girasoles lejanos de fondo -->
  <div class="capa-flores capa-fondo" aria-hidden="true">
    {#each FLORES_FONDO as f (f.id)}
      <div
        class="flor-fondo-item"
        style="
          left: {f.left}%;
          --delay-brote: {f.delay}s;
          --rot-base: {f.rot}deg;
        "
      >
        <div
          class="flor-sway-fondo"
          style="--rot-sway: {((sway - 0.5) * 4).toFixed(1)}deg;"
        >
          <img
            src="/images/girasol.png"
            alt="Girasol de fondo"
            style="height: {f.height}px;"
            class="img-flor-fondo"
          />
        </div>
      </div>
    {/each}
  </div>

  <!-- CAPA 2: Jazmines silvestres blancos entre las hierbas -->
  <div class="capa-jazmines" aria-hidden="true">
    {#each JAZMINES_SILVESTRES as j (j.id)}
      <div
        class="jazmin-item"
        style="
          left: {j.left}%;
          bottom: {j.bottom}px;
          --delay-jazmin: {j.delay}s;
        "
      >
        <svg viewBox="0 0 40 40" width="34" height="34" class="jazmin-svg">
          <!-- Pétalos blancos delicados -->
          {#each Array(5) as _, p}
            <ellipse
              cx="20"
              cy="10"
              rx="5"
              ry="9"
              fill="#ffffff"
              opacity="0.95"
              transform="rotate({p * 72} 20 20)"
            />
          {/each}
          <!-- Centro dorado del jazmín -->
          <circle cx="20" cy="20" r="3.5" fill="#f5b031" />
        </svg>
      </div>
    {/each}
  </div>

  <!-- Totoro mirando hacia arriba a la derecha, asomado detrás de la fila más frontal de girasoles -->
  <div class="totoro-jardin-contenedor" aria-hidden="true">
    <img
      src="/images/totoro-mira-arriba.png"
      alt="Totoro mirando hacia el cielo estrellado"
      class="totoro-jardin-img"
    />
  </div>

  <!-- CAPA 3: Girasoles protagonistas en primer plano -->
  <div class="capa-flores capa-frente">
    {#each FLORES_FRENTE as f (f.id)}
      {@const inclinada = florInclinada === f.id}
      <div
        class="flor-frente-item"
        class:flor-inclinada={inclinada}
        style="
          left: {f.left}%;
          --delay-brote: {f.delay}s;
          --rot-base: {f.rotBase}deg;
        "
        onclick={() => tocarFlor(f.id)}
        onkeydown={(e) => e.key === 'Enter' && tocarFlor(f.id)}
        role="button"
        tabindex="0"
        aria-label="Flor de girasol primaveral"
      >
        <div
          class="flor-sway-frente"
          style="--sway-angulo: {((sway - 0.5) * 5).toFixed(1)}deg;"
        >
          <img
            src="/images/girasol.png"
            alt="Girasol en flor"
            style="width: {f.width}px;"
            class="img-flor-frente"
          />
        </div>
      </div>
    {/each}
  </div>
</div>

<style>
  .jardin-escena {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 580px;
    z-index: 12;
    pointer-events: none;
    overflow: visible;
  }

  /* Resplandor cálido que despierta con el jardín */
  .resplandor-tierra {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 110vw;
    height: 220px;
    background: radial-gradient(
      ellipse at 50% 100%,
      rgba(242, 177, 53, 0.28) 0%,
      rgba(154, 98, 166, 0.15) 50%,
      transparent 80%
    );
    opacity: 0;
    transition: opacity 2.5s cubic-bezier(0.16, 1, 0.3, 1);
    filter: blur(25px);
  }

  .jardin-visible .resplandor-tierra {
    opacity: 1;
  }

  /* Capas de flores */
  .capa-flores {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  .capa-fondo {
    z-index: 1;
    filter: blur(1.5px) brightness(0.85);
    opacity: 0.9;
  }

  .capa-frente {
    z-index: 3;
    pointer-events: auto;
  }

  /* Animación de brote y oscilación de flores lejanas */
  .flor-fondo-item {
    position: absolute;
    bottom: 0;
    transform-origin: bottom center;
    transform: translateX(-50%) translateY(120%) scaleY(0.4);
    opacity: 0;
    will-change: transform, opacity;
  }

  .jardin-visible .flor-fondo-item {
    animation: brotarFondo 2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    animation-delay: var(--delay-brote);
  }

  @keyframes brotarFondo {
    0% {
      transform: translateX(-50%) translateY(120%) scaleY(0.4);
      opacity: 0;
    }
    100% {
      transform: translateX(-50%) translateY(0) scaleY(1);
      opacity: 0.85;
    }
  }

  .flor-sway-fondo {
    transform-origin: bottom center;
    transform: rotate(calc(var(--rot-base) + var(--rot-sway)));
    transition: transform 0.4s ease-out;
  }

  .img-flor-fondo {
    width: auto;
    object-fit: contain;
  }

  /* Jazmines silvestres */
  .capa-jazmines {
    position: absolute;
    inset: 0;
    z-index: 2;
  }

  .jazmin-item {
    position: absolute;
    transform: translateX(-50%) scale(0);
    opacity: 0;
    transition:
      transform 1.6s cubic-bezier(0.34, 1.56, 0.64, 1),
      opacity 1.2s ease;
    transition-delay: var(--delay-jazmin);
    filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.6));
    animation: mecerJazmin 4s ease-in-out infinite alternate;
  }

  .jardin-visible .jazmin-item {
    transform: translateX(-50%) scale(1);
    opacity: 0.95;
  }

  @keyframes mecerJazmin {
    0% { transform: translateX(-50%) scale(1) rotate(-4deg); }
    100% { transform: translateX(-50%) scale(1) rotate(4deg); }
  }

  /* Girasoles de primer plano: brote fluido en GPU desacoplado del audio */
  .flor-frente-item {
    position: absolute;
    bottom: -10px;
    transform-origin: bottom center;
    transform: translateX(-50%) translateY(120%) scale(0.5);
    opacity: 0;
    cursor: pointer;
    will-change: transform, opacity;
    -webkit-tap-highlight-color: transparent;
  }

  .jardin-visible .flor-frente-item {
    animation: brotarFrente 2.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    animation-delay: var(--delay-brote);
  }

  @keyframes brotarFrente {
    0% {
      transform: translateX(-50%) translateY(120%) scale(0.4);
      opacity: 0;
    }
    60% {
      opacity: 1;
    }
    100% {
      transform: translateX(-50%) translateY(0) scale(1);
      opacity: 1;
    }
  }

  /* Vaivén reactivo a la música desacoplado del brote */
  .flor-sway-frente {
    transform-origin: bottom center;
    transform: rotate(calc(var(--rot-base) + var(--sway-angulo)));
    transition: transform 0.45s ease-out;
  }

  .flor-frente-item:hover .flor-sway-frente,
  .flor-frente-item.flor-inclinada .flor-sway-frente {
    transform: translateY(-6px) scale(1.05) rotate(calc(var(--rot-base) + 7deg)) !important;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .img-flor-frente {
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 4px 22px rgba(242, 177, 53, 0.4));
    user-select: none;
    -webkit-user-select: none;
  }

  /* Totoro asomado a la derecha detrás de los girasoles frontales */
  .totoro-jardin-contenedor {
    position: absolute;
    bottom: -15px;
    right: 4%;
    z-index: 2; /* Justo detrás de capa-frente (z-index: 3) */
    pointer-events: none;
    transform: translateY(115%) scale(0.8);
    opacity: 0;
    will-change: transform, opacity;
  }

  .jardin-visible .totoro-jardin-contenedor {
    animation: asomarTotoro 3.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    animation-delay: 1.5s; /* Aparece tiernamente después de que brota el jardín */
  }

  @keyframes asomarTotoro {
    0% {
      transform: translateY(115%) scale(0.8);
      opacity: 0;
    }
    100% {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }

  .totoro-jardin-img {
    width: 234px; /* Más grande y majestuoso */
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 6px 24px rgba(0, 0, 0, 0.45));
    animation: mecerTotoro 7s ease-in-out infinite alternate;
  }

  @keyframes mecerTotoro {
    0% { transform: rotate(-1.2deg) translateY(0); }
    100% { transform: rotate(1.2deg) translateY(-5px); }
  }

  /* Pétalos dorados flotando hacia el cielo */
  .petalos-viento {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 10;
  }

  .petalo-dorado {
    position: absolute;
    bottom: 40px;
    border-radius: 50% 0 50% 50%;
    background: radial-gradient(circle, #fff3a8 20%, #f2b135 80%);
    box-shadow: 0 0 10px rgba(255, 220, 110, 0.75);
    opacity: 0;
    will-change: transform, opacity;
    animation: elevarPetalo linear infinite;
  }

  @keyframes elevarPetalo {
    0% {
      opacity: 0;
      transform: translate3d(0, 0, 0) rotate(0deg) scale(0.5);
    }
    12% {
      opacity: 0.9;
    }
    60% {
      opacity: 0.8;
    }
    85% {
      opacity: 0.5;
    }
    100% {
      opacity: 0;
      /* Sube hasta aproximadamente el 65-70vh desde abajo, justo a la altura de la caja de texto */
      transform: translate3d(55px, -62vh, 0) rotate(360deg) scale(1.15);
    }
  }

  /* Ajustes responsivos para celular */
  @media (max-width: 480px) {
    .jardin-escena {
      height: 290px;
    }
    .flor-frente-item {
      bottom: -15px;
    }
    .img-flor-frente {
      transform: scale(0.78);
    }
    .img-flor-fondo {
      transform: scale(0.75);
    }
    .totoro-jardin-contenedor {
      right: 2%;
      bottom: -15px;
    }
    .totoro-jardin-img {
      width: 228px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
    }
  }
</style>

